import { getAdobeAccessToken } from '../auth/adobeIms';

export const TRACKER_ACTION_BASE_URL = 'https://55338-856salmonanaconda-stage.adobeioruntime.net/api/v1/web/ondemand-tracker';

export interface TrackerUser {
  _id: string;        // email (used as primary key)
  name?: string;
  accessLevel: string;
  createdAt?: string;
  lastUpdated?: string;
}

export interface TrackerAuditLogEntry {
  _id: string;
  timestamp: string;
  actionType: string;
  actorId: string;
  accessLevel?: string;
  [key: string]: unknown;
}

export interface TrackerAuditLogResponse {
  logs: TrackerAuditLogEntry[];
  totalCount: number;
  offset: number;
  limit: number;
}

function trackerActionUrl(action: string): string {
  return `${TRACKER_ACTION_BASE_URL.replace(/\/$/, '')}/${action}`;
}

function authHeaders(token: string): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

/**
 * Fetch all users from the tracker app.
 * Calls the tracker's admin-list-users action.
 */
export async function trackerListUsers(): Promise<TrackerUser[]> {
  try {
    const token = getAdobeAccessToken();
    const response = await fetch(trackerActionUrl('admin-list-users'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({ error: 'unknown' }));
      throw new Error((errBody as { error?: string }).error || `HTTP ${response.status}`);
    }
    const data = await response.json() as { users: TrackerUser[] };
    return data.users ?? [];
  } catch (error) {
    console.error('trackerListUsers error:', error);
    throw error;
  }
}

/**
 * Upsert a user in the tracker app.
 * Calls the tracker's admin-manage-users action with operation: 'upsert'.
 */
export async function trackerUpsertUser(user: {
  email: string;
  name?: string;
  accessLevel: string;
}): Promise<void> {
  try {
    const token = getAdobeAccessToken();
    const response = await fetch(trackerActionUrl('admin-manage-users'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({ operation: 'upsert', user }),
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({ error: 'unknown' }));
      throw new Error((errBody as { error?: string }).error || `HTTP ${response.status}`);
    }
  } catch (error) {
    console.error('trackerUpsertUser error:', error);
    throw error;
  }
}

/**
 * Fetch audit log entries from the tracker app.
 * Calls the tracker's admin-list-audit-logs action.
 */
export async function trackerListAuditLogs(params: {
  offset?: number;
  limit?: number;
  actionType?: string;
  actorEmail?: string;
  startDate?: string;
  endDate?: string;
} = {}): Promise<TrackerAuditLogResponse> {
  try {
    const token = getAdobeAccessToken();
    const response = await fetch(trackerActionUrl('admin-list-audit-logs'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({ error: 'unknown' }));
      throw new Error((errBody as { error?: string }).error || `HTTP ${response.status}`);
    }
    return await response.json() as TrackerAuditLogResponse;
  } catch (error) {
    console.error('trackerListAuditLogs error:', error);
    throw error;
  }
}
